var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useState, useEffect, useCallback, useRef } from 'react';
import { TokenManager } from '../utils/TokenManager';
import { resolveSuperAdminFlags, isSuperAdminUser } from '../utils/superAdmin';
/**
 * 初始化认证状态（检查本地缓存）
 */
var getInitialAuthState = function (config) {
    // 先设置TokenManager的key，以便正确获取token
    TokenManager.setTokenKey(config.tokenKey, config.refreshTokenKey);
    var isEmbedded = typeof window !== 'undefined' && window.parent !== window;
    /**
     * iframe 内绝不信任本地缓存「已登录」直出：否则路由会在同源 POST /api/auth/verify 完成前挂载，
     * 仪表盘等会抢先发 GET /api/...，出现无 Authorization 或竞态 401。
     * Token 仍留在 localStorage，checkAuth / applyTokenFromParent 会照常读取并验票。
     */
    if (isEmbedded) {
        return {
            isAuthenticated: false,
            token: null,
            user: null,
            loading: true,
            error: null,
            permissions: [],
        };
    }
    // 直接检查localStorage，不依赖TokenManager（因为TokenManager可能还没初始化）
    // 检查多个可能的token key
    var token = localStorage.getItem(config.tokenKey) ||
        sessionStorage.getItem(config.tokenKey) ||
        localStorage.getItem('mslogin_token') ||
        sessionStorage.getItem('mslogin_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('token');
    // 检查缓存的用户信息
    var cachedUser = localStorage.getItem('mslogin_user');
    // 如果有token和缓存的用户信息，使用缓存，避免显示loading
    if (token && cachedUser) {
        try {
            var userData = JSON.parse(cachedUser);
            var permissions = userData.permissions || [];
            // 确保用户数据格式正确
            if (userData && (userData.id || userData.account || userData.adminId)) {
                console.log('[useAuthWithConfig] 使用缓存初始化状态:', {
                    hasToken: !!token,
                    hasUser: !!userData,
                    permissionsCount: permissions.length
                });
                return {
                    isAuthenticated: true,
                    token: token,
                    user: userData,
                    loading: false,
                    error: null,
                    permissions: permissions,
                };
            }
            else {
                console.warn('[useAuthWithConfig] 缓存用户数据格式不正确:', userData);
            }
        }
        catch (error) {
            console.warn('[useAuthWithConfig] 缓存用户信息解析失败，使用默认状态:', error);
        }
    }
    else {
        // 即使没有缓存，如果有token，也先设置为已认证（但loading为true）
        // 这样可以避免显示"加载中"，让后台验证
        if (token) {
            console.log('[useAuthWithConfig] 有token但没有用户缓存，使用临时状态:', {
                hasToken: !!token,
                hasCachedUser: !!cachedUser
            });
            // 尝试从token中解析用户信息
            try {
                var payload = JSON.parse(atob(token.split('.')[1]));
                var permissions = payload.permissions || [];
                var base = {
                    id: payload.adminId || payload.id,
                    account: payload.account,
                    admin_name: payload.account,
                    roles: payload.roles || [],
                    permissions: permissions,
                    is_super_admin: payload.is_super_admin,
                    isSuperAdmin: payload.isSuperAdmin,
                };
                var userData = __assign(__assign({}, base), resolveSuperAdminFlags(base));
                // 保存到缓存
                localStorage.setItem('mslogin_user', JSON.stringify(userData));
                return {
                    isAuthenticated: true,
                    token: token,
                    user: userData,
                    loading: false,
                    error: null,
                    permissions: userData.permissions,
                };
            }
            catch (parseError) {
                console.warn('[useAuthWithConfig] 无法从token解析用户信息:', parseError);
            }
        }
        else {
            console.log('[useAuthWithConfig] 没有缓存和token，使用默认状态:', {
                hasToken: !!token,
                hasCachedUser: !!cachedUser
            });
        }
    }
    // 没有缓存时，返回默认状态
    return {
        isAuthenticated: false,
        token: null,
        user: null,
        loading: true,
        error: null,
        permissions: [],
    };
};
/**
 * 配置化的认证Hook
 * 支持不同微服务的认证配置
 */
var isAuthDebug = typeof process !== 'undefined' && process.env.NODE_ENV === 'development';
export var useAuthWithConfig = function (config) {
    if (isAuthDebug) {
        console.log('[useAuthWithConfig] Hook 开始初始化，配置:', config);
    }
    // 在初始化时就检查缓存
    var _a = useState(function () { return getInitialAuthState(config); }), authState = _a[0], setAuthState = _a[1];
    /** 用 ref 防抖复入，避免 isChecking 进入 useCallback 依赖导致 identity 变化 → message 监听 effect 反复重绑 */
    var isCheckingRef = useRef(false);
    if (isAuthDebug) {
        console.log('[useAuthWithConfig] 初始状态:', authState);
    }
    // 设置TokenManager的key
    useEffect(function () {
        TokenManager.setTokenKey(config.tokenKey, config.refreshTokenKey);
    }, [config.tokenKey, config.refreshTokenKey]);
    // 检查认证状态
    var checkAuth = useCallback(function (silent) {
        if (silent === void 0) { silent = false; }
        return __awaiter(void 0, void 0, void 0, function () {
            var token, trimmedVerify, authApiUrl, response, result, adminData, permissions, userData, status, verifyError_1, payload, roles, permissions, rawJwt, userData, error_1;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        // 防止重复调用
                        if (isCheckingRef.current) {
                            if (isAuthDebug) {
                                console.log('[useAuthWithConfig] 认证检查正在进行中，跳过重复调用');
                            }
                            return [2 /*return*/];
                        }
                        if (isAuthDebug) {
                            console.log('[useAuthWithConfig] checkAuth 函数被调用', silent ? '(静默模式)' : '');
                        }
                        isCheckingRef.current = true;
                        // 静默模式下不设置loading，避免页面闪烁
                        if (!silent) {
                            setAuthState(function (prev) { return (__assign(__assign({}, prev), { loading: true, error: null })); });
                        }
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 7, 8, 9]);
                        token = TokenManager.getCurrentToken();
                        console.log('[useAuthWithConfig] Token检查结果:', {
                            hasToken: !!token,
                            tokenLength: token ? token.length : 0,
                            tokenPreview: token ? token.substring(0, 20) + '...' : 'null',
                            config: {
                                serviceName: config.serviceName,
                                apiBaseUrl: config.apiBaseUrl,
                                authEndpoint: config.authEndpoint,
                            }
                        });
                        if (!token) {
                            console.log('[useAuthWithConfig] 没有找到token，认证失败');
                            setAuthState({
                                isAuthenticated: false,
                                token: null,
                                user: null,
                                loading: false,
                                error: null,
                                permissions: [],
                            });
                            return [2 /*return*/];
                        }
                        _c.label = 2;
                    case 2:
                        _c.trys.push([2, 5, , 6]);
                        trimmedVerify = (_a = config.authVerifyBaseUrl) === null || _a === void 0 ? void 0 : _a.trim().replace(/\/$/, '');
                        authApiUrl = trimmedVerify ||
                            (config.msLoginUrl || '').trim().replace(/\/$/, '') ||
                            (config.apiBaseUrl || '').trim().replace(/\/$/, '');
                        console.log('[useAuthWithConfig] 使用API配置:', {
                            apiBaseUrl: config.apiBaseUrl,
                            msLoginUrl: config.msLoginUrl,
                            authEndpoint: config.authEndpoint,
                            authApiUrl: authApiUrl
                        });
                        console.log('[useAuthWithConfig] 发送认证请求到:', "".concat(authApiUrl).concat(config.authEndpoint));
                        return [4 /*yield*/, fetch("".concat(authApiUrl).concat(config.authEndpoint), {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({ token: token }),
                            })];
                    case 3:
                        response = _c.sent();
                        console.log('[useAuthWithConfig] 认证响应状态:', response.status);
                        return [4 /*yield*/, response.json()];
                    case 4:
                        result = _c.sent();
                        console.log('[useAuthWithConfig] 认证响应结果:', result);
                        if (result.success && ((_b = result.data) === null || _b === void 0 ? void 0 : _b.valid)) {
                            console.log('[useAuthWithConfig] 认证成功，设置认证状态');
                            adminData = result.data.admin || result.data.payload;
                            permissions = (adminData === null || adminData === void 0 ? void 0 : adminData.permissions) || [];
                            if (!adminData) {
                                console.warn('[useAuthWithConfig] 认证响应中没有用户数据');
                                TokenManager.clearAll();
                                setAuthState({
                                    isAuthenticated: false,
                                    token: null,
                                    user: null,
                                    loading: false,
                                    error: '认证响应格式错误',
                                    permissions: [],
                                });
                                return [2 /*return*/];
                            }
                            userData = __assign(__assign({}, adminData), resolveSuperAdminFlags(adminData));
                            setAuthState({
                                isAuthenticated: true,
                                token: token,
                                user: userData,
                                loading: false,
                                error: null,
                                permissions: permissions,
                            });
                            // 立即保存用户信息到 localStorage，供其他实例使用
                            try {
                                localStorage.setItem('mslogin_user', JSON.stringify(userData));
                            }
                            catch (error) {
                                console.warn('[useAuthWithConfig] 保存用户信息到localStorage失败:', error);
                            }
                            console.log('[useAuthWithConfig] 认证状态已设置:', {
                                isAuthenticated: true,
                                hasToken: !!token,
                                hasUser: !!adminData,
                                permissionsCount: permissions.length
                            });
                        }
                        else {
                            status = response.status;
                            if (silent && status >= 500) {
                                return [2 /*return*/];
                            }
                            TokenManager.clearAll();
                            try {
                                localStorage.removeItem('mslogin_user');
                            }
                            catch (_d) {
                                /* ignore */
                            }
                            setAuthState({
                                isAuthenticated: false,
                                token: null,
                                user: null,
                                loading: false,
                                error: (result && result.message) ||
                                    (status === 401 || status === 403
                                        ? 'Token无效或已过期（请确认 CRM 后端 MSLOGIN_JWT_SECRET 与 msLogin JWT_SECRET 一致）'
                                        : "\u9A8C\u7968\u5931\u8D25 (".concat(status, ")")),
                                permissions: [],
                            });
                        }
                        return [3 /*break*/, 6];
                    case 5:
                        verifyError_1 = _c.sent();
                        // 如果验证失败，可能是网络问题或服务不可用
                        // 暂时假设token有效，避免频繁的网络请求
                        console.warn('Token验证失败，使用本地token:', verifyError_1);
                        // 尝试从token中解析用户信息
                        try {
                            payload = JSON.parse(atob(token.split('.')[1]));
                            // 安全检查：确保 payload 和必要字段存在
                            if (!payload || !payload.adminId || !payload.account) {
                                throw new Error('Token payload 格式无效');
                            }
                            roles = payload.roles || [];
                            permissions = payload.permissions || [];
                            rawJwt = __assign(__assign({}, payload), { permissions: permissions, roles: roles });
                            userData = __assign({ id: payload.adminId, account: payload.account, admin_name: payload.account, roles: roles, permissions: permissions }, resolveSuperAdminFlags(rawJwt));
                            setAuthState({
                                isAuthenticated: true,
                                token: token,
                                user: userData,
                                loading: false,
                                error: null,
                                permissions: permissions,
                            });
                            // 立即保存用户信息到 localStorage，供其他实例使用
                            try {
                                localStorage.setItem('mslogin_user', JSON.stringify(userData));
                            }
                            catch (error) {
                                console.warn('[useAuthWithConfig] 保存用户信息到localStorage失败:', error);
                            }
                        }
                        catch (parseError) {
                            console.error('[useAuthWithConfig] Token解析失败:', parseError);
                            setAuthState({
                                isAuthenticated: false,
                                token: null,
                                user: null,
                                loading: false,
                                error: parseError instanceof Error ? parseError.message : 'Token格式无效',
                                permissions: [],
                            });
                        }
                        return [3 /*break*/, 6];
                    case 6: return [3 /*break*/, 9];
                    case 7:
                        error_1 = _c.sent();
                        // 静默模式下，验证失败时不更新状态，保持当前状态
                        if (!silent) {
                            setAuthState({
                                isAuthenticated: false,
                                token: null,
                                user: null,
                                loading: false,
                                error: error_1 instanceof Error ? error_1.message : '认证检查失败',
                                permissions: [],
                            });
                        }
                        return [3 /*break*/, 9];
                    case 8:
                        isCheckingRef.current = false;
                        return [7 /*endfinally*/];
                    case 9: return [2 /*return*/];
                }
            });
        });
    }, [
        config.apiBaseUrl,
        config.authVerifyBaseUrl,
        config.authEndpoint,
        config.serviceName,
        config.tokenKey,
        config.refreshTokenKey,
        config.msLoginUrl,
    ]);
    /**
     * msLogin ContainerPage iframe 场景：子域（如 crm.homaxs.com）与父页（www.homaxs.com）不同源，
     * 无法共享 localStorage。父页通过 postMessage 下发 TOKEN_PROVIDE，此处写入 TokenManager 并验票。
     * 与 msDevice/msSpace 等应用内手写逻辑对齐，集中到 shared-auth 供 mscrm 等使用。
     */
    useEffect(function () {
        if (typeof window === 'undefined')
            return;
        var applyTokenFromParent = function (token) {
            if (!token || typeof token !== 'string')
                return;
            TokenManager.setTokenKey(config.tokenKey, config.refreshTokenKey);
            // 与「本地已持有相同 token」去重；401 清空 crm_token 后父页再发同一 JWT 时必须重新写入，不能按 ref 去重跳过
            var existing = TokenManager.getCurrentToken();
            if (existing === token) {
                return;
            }
            TokenManager.setToken(token);
            try {
                localStorage.setItem('mslogin_token', token);
            }
            catch (_a) {
                // ignore quota / private mode
            }
            // 静默验票：避免与父页多条 TOKEN_* 叠加导致 loading 反复为 true（页面一直转圈）
            void checkAuth(true);
        };
        var handleMessage = function (event) {
            var _a, _b, _c;
            var t = (_a = event.data) === null || _a === void 0 ? void 0 : _a.token;
            var type = (_b = event.data) === null || _b === void 0 ? void 0 : _b.type;
            if (!t || typeof t !== 'string')
                return;
            if (type === 'TOKEN_PROVIDE') {
                var svc = (_c = event.data) === null || _c === void 0 ? void 0 : _c.service;
                if (svc && config.serviceName && svc !== config.serviceName) {
                    return;
                }
                applyTokenFromParent(t);
                return;
            }
            if (type === 'TOKEN_UPDATE' || type === 'TOKEN_UPDATED') {
                applyTokenFromParent(t);
            }
        };
        window.addEventListener('message', handleMessage);
        return function () { return window.removeEventListener('message', handleMessage); };
    }, [config.tokenKey, config.refreshTokenKey, config.serviceName, checkAuth]);
    useEffect(function () {
        if (typeof window === 'undefined')
            return;
        if (window.parent === window)
            return;
        var send = function () {
            window.parent.postMessage({ type: 'TOKEN_UPDATE_REQUEST' }, '*');
        };
        send();
        var n = 0;
        var id = window.setInterval(function () {
            n += 1;
            send();
            if (n >= 8)
                window.clearInterval(id);
        }, 2000);
        return function () { return window.clearInterval(id); };
    }, []);
    // 登录
    var login = useCallback(function (token) { return __awaiter(void 0, void 0, void 0, function () {
        var error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    TokenManager.setToken(token);
                    return [4 /*yield*/, checkAuth()];
                case 1:
                    _a.sent();
                    return [3 /*break*/, 3];
                case 2:
                    error_2 = _a.sent();
                    setAuthState(function (prev) { return (__assign(__assign({}, prev), { loading: false, error: error_2 instanceof Error ? error_2.message : '登录失败' })); });
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [checkAuth]);
    // 登出
    var logout = useCallback(function () {
        TokenManager.clearAll();
        setAuthState({
            isAuthenticated: false,
            token: null,
            user: null,
            loading: false,
            error: null,
            permissions: [],
        });
    }, []);
    // 权限检查函数
    var hasPermission = useCallback(function (permission) {
        if (!authState.isAuthenticated || !authState.user)
            return false;
        // 超级管理员拥有所有权限
        if (isSuperAdminUser(authState.user))
            return true;
        // 检查具体权限
        var userPermissions = authState.permissions || [];
        // 直接匹配
        if (userPermissions.includes(permission))
            return true;
        // 通配符匹配：检查是否有通配符权限
        for (var _i = 0, userPermissions_1 = userPermissions; _i < userPermissions_1.length; _i++) {
            var userPerm = userPermissions_1[_i];
            if (userPerm.endsWith(':*')) {
                // 提取通配符前缀
                var wildcardPrefix = userPerm.slice(0, -2); // 移除 ':*'
                if (permission.startsWith(wildcardPrefix + ':')) {
                    return true;
                }
            }
        }
        return false;
    }, [authState.isAuthenticated, authState.user, authState.permissions]);
    var hasAnyPermission = useCallback(function (permissions) {
        if (!authState.isAuthenticated || !authState.user) {
            console.log('[useAuthWithConfig] hasAnyPermission: 未认证或用户不存在', {
                isAuthenticated: authState.isAuthenticated,
                hasUser: !!authState.user,
            });
            return false;
        }
        // 超级管理员拥有所有权限
        if (isSuperAdminUser(authState.user)) {
            console.log('[useAuthWithConfig] hasAnyPermission: 超级管理员，拥有所有权限');
            return true;
        }
        // 检查是否有任一权限
        var userPermissions = authState.permissions || [];
        console.log('[useAuthWithConfig] hasAnyPermission: 检查权限', {
            requiredPermissions: permissions,
            userPermissions: userPermissions,
            userRoles: authState.user.roles || [],
        });
        var result = permissions.some(function (permission) {
            // 直接匹配
            if (userPermissions.includes(permission)) {
                console.log("[useAuthWithConfig] hasAnyPermission: \u76F4\u63A5\u5339\u914D\u6743\u9650 '".concat(permission, "'"));
                return true;
            }
            // 通配符匹配
            for (var _i = 0, userPermissions_2 = userPermissions; _i < userPermissions_2.length; _i++) {
                var userPerm = userPermissions_2[_i];
                if (userPerm.endsWith(':*')) {
                    var wildcardPrefix = userPerm.slice(0, -2);
                    if (permission.startsWith(wildcardPrefix + ':')) {
                        console.log("[useAuthWithConfig] hasAnyPermission: \u901A\u914D\u7B26\u5339\u914D '".concat(userPerm, "' -> '").concat(permission, "'"));
                        return true;
                    }
                }
            }
            return false;
        });
        console.log('[useAuthWithConfig] hasAnyPermission: 最终结果', result);
        return result;
    }, [authState.isAuthenticated, authState.user, authState.permissions]);
    var hasAllPermissions = useCallback(function (permissions) {
        if (!authState.isAuthenticated || !authState.user)
            return false;
        // 超级管理员拥有所有权限
        if (isSuperAdminUser(authState.user))
            return true;
        // 检查是否有所有权限
        var userPermissions = authState.permissions || [];
        return permissions.every(function (permission) {
            // 直接匹配
            if (userPermissions.includes(permission))
                return true;
            // 通配符匹配
            for (var _i = 0, userPermissions_3 = userPermissions; _i < userPermissions_3.length; _i++) {
                var userPerm = userPermissions_3[_i];
                if (userPerm.endsWith(':*')) {
                    var wildcardPrefix = userPerm.slice(0, -2);
                    if (permission.startsWith(wildcardPrefix + ':')) {
                        return true;
                    }
                }
            }
            return false;
        });
    }, [authState.isAuthenticated, authState.user, authState.permissions]);
    useEffect(function () {
        // 检查初始状态是否使用了缓存
        var token = TokenManager.getCurrentToken();
        var cachedUser = localStorage.getItem('mslogin_user');
        var hasCache = token && cachedUser;
        if (hasCache && !authState.loading && authState.isAuthenticated) {
            // 有缓存且已设置，延迟1秒后静默验证，避免影响页面切换
            var timer_1 = setTimeout(function () {
                checkAuth(true).catch(function () {
                    // 验证失败时保持缓存状态，不强制跳转
                });
            }, 1000);
            return function () { return clearTimeout(timer_1); };
        }
        else if (!hasCache) {
            var embedded = typeof window !== 'undefined' && window.parent !== window;
            if (embedded) {
                // 等父窗口 TOKEN_UPDATE_REQUEST → TOKEN_PROVIDE（IframeRenderer 约 1s 后下发）
                var timer_2 = setTimeout(function () {
                    // 静默：避免与父页已下发的 token 叠加时再顶一次 loading
                    checkAuth(true);
                }, 1200);
                return function () { return clearTimeout(timer_2); };
            }
            checkAuth();
        }
        else {
            /**
             * 有 token+mslogin_user，但未命中「已认证且非 loading」首分支时（常见：iframe 首屏强制未登录；
             * 或独立窗口缓存异常），必须主动验票，否则会一直 loading / 卡在「正在验证登录…」。
             */
            var embedded = typeof window !== 'undefined' && window.parent !== window;
            if (embedded) {
                void checkAuth(true);
            }
            else {
                checkAuth();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // 只在组件挂载时执行一次
    return __assign(__assign({}, authState), { checkAuth: checkAuth, login: login, logout: logout, hasPermission: hasPermission, hasAnyPermission: hasAnyPermission, hasAllPermissions: hasAllPermissions });
};
